import {useState, useRef, useEffect, useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';

export function useStateWithRefAndCallback(initialState, callback) {
  const [state, setState] = useState(initialState);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
    if (callback) {
      callback(state);
      // eslint-disable-next-line no-param-reassign, react-hooks/exhaustive-deps
      callback = null;
    }
  }, [state]);

  return [state, setState, stateRef];
}

const ClickOutsideOptionalParameter = {
  useCapture: false,
};

export function useClickOutside(
  refContainer,
  onClickOutside,
  excludedComponentRef = [],
  optionalParam = ClickOutsideOptionalParameter,
) {
  const {useCapture} = optionalParam;

  useEffect(() => {
    const handleClickOutside = (event) => {
      // outside click
      if (refContainer?.current && !refContainer.current.contains(event.target)) {
        let existInExcludedComponent = false;
        for (let i = 0; i < excludedComponentRef?.length; i += 1) {
          if (excludedComponentRef[i]?.current && excludedComponentRef[i].current.contains(event.target)) {
            existInExcludedComponent = true;
            break;
          }
        }
        if (onClickOutside && !existInExcludedComponent) onClickOutside(event.target);
      }
    };

    // add when mounted
    document.addEventListener('mousedown', handleClickOutside, useCapture);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, useCapture);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refContainer, onClickOutside, excludedComponentRef]);
}

export function useMouseUpOutside(refContainer, onMouseUpOutside) {
  useEffect(() => {
    const handleMouseUpOutside = (event) => {
      // outside click
      if (refContainer?.current && !refContainer.current.contains(event.target)) {
        if (onMouseUpOutside) onMouseUpOutside();
      }
    };

    // add when mounted
    document.addEventListener('mouseup', handleMouseUpOutside);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener('mouseup', handleMouseUpOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refContainer, onMouseUpOutside]);
}

export function useEnterPress(ref, onEnter) {
  useEffect(() => {
    const handleEnter = (e) => {
      if (ref?.current) {
        if (e.keyCode === 13) {
          onEnter(e);
          // eslint-disable-next-line no-param-reassign
          // onEnter = null;
        }
      }
    };

    // add when mounted
    document.addEventListener('keypress', handleEnter);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener('keypress', handleEnter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, onEnter]);
}

export function useCancellablePromises() {
  const pendingPromises = useRef([]);

  // eslint-disable-next-line no-return-assign
  const appendPendingPromise = (promise) => (pendingPromises.current = [...pendingPromises.current, promise]);

  // eslint-disable-next-line no-return-assign
  const removePendingPromise = (promise) =>
    (pendingPromises.current = pendingPromises.current.filter((p) => p !== promise));

  const clearPendingPromises = () => pendingPromises.current.map((p) => p.cancel());

  const api = {
    appendPendingPromise,
    removePendingPromise,
    clearPendingPromises,
  };

  return api;
}

export function useArrowUpPress(ref, onArrowUpPress) {
  useEffect(() => {
    const handleArrowUpPress = (e) => {
      if (ref?.current) {
        if (e.keyCode === 38 && onArrowUpPress) {
          onArrowUpPress(e);
        }
      }
    };

    // add when mounted
    document.addEventListener('keydown', handleArrowUpPress);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener('keydown', handleArrowUpPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, onArrowUpPress]);
}

export function useArrowDownPress(ref, onArrowDownPress) {
  useEffect(() => {
    const handleArrowDownPress = (e) => {
      if (ref?.current) {
        if (e.keyCode === 40 && onArrowDownPress) {
          onArrowDownPress(e);
        }
      }
    };

    // add when mounted
    document.addEventListener('keydown', handleArrowDownPress);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener('keydown', handleArrowDownPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, onArrowDownPress]);
}

export const useStaticCallback = (callback) => {
  const callbackRef = useRef();

  useEffect(() => {
    callbackRef.current = callback;
  });

  return useCallback((...params) => {
    callbackRef.current(...params);
  }, []);
};

export function useKeyPress(targetKey, disabled = false) {
  const [keyPressed, setKeyPressed] = useState(false);

  function downHandler(event) {
    if (event.key === targetKey) {
      setKeyPressed(true);
    }
  }

  function upHandler(event) {
    if (event.key === targetKey) {
      setKeyPressed(false);
    }
  }

  // set keyPressed to false if disabled
  useEffect(() => {
    if (disabled) setKeyPressed(false);
  }, [keyPressed, disabled]);

  // Add event listeners
  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return keyPressed;
}

export function useHybridClicks(click, doubleClick, timeout = 200) {
  const clickTimeout = useRef();

  return useCallback(
    (event) => {
      if (click) click(event);

      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = undefined;

        if (doubleClick) doubleClick(event);
      } else {
        clickTimeout.current = setTimeout(() => {
          clearTimeout(clickTimeout.current);
          clickTimeout.current = undefined;
        }, timeout);
      }
    },
    [click, doubleClick, timeout],
  );
}

export const useResizable = (handler = () => {}) => {
  // Add event listeners
  useEffect(() => {
    window.addEventListener('resize', handler);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('resize', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useDispatchAndAddUntoItem = () => {
  const dispatch = useDispatch();
  return (func) =>
    dispatch(async (dispatchReturn, getState) => {
      if (!func) return null;

      const res = await func(dispatchReturn, getState);
      dispatch(globalAddUndoItem());
      return res;
    });
};
